import { useRecoilValue } from 'recoil';
import { GetSessionError } from '~/backend/connector-api/connector-components';
import { SessionPresentation } from '~/backend/connector-api/connector-schemas';
import {
    APIType,
    ContouringCustomizationFile,
    UserCustomization,
} from '~/backend/types';
import { WorkflowTargetIds } from '~/hooks/workflows/useWorkflowTargetIds';
import {
    useBackendModels,
    useModelCustomizations,
} from '~/hooks/workflows/backend/useBackend';
import useAutoSegmentationTask from '~/hooks/workflows/connector/tasks/useAutoSegmentationTask';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import AutoSegmentation from './AutoSegmentation';

type ContourConnectorWorkflowProps = {
    sessionData: SessionPresentation | undefined;
    environmentObjectIds: WorkflowTargetIds | undefined;
    isLoadingSession: boolean;
    error: Error | GetSessionError | null;
};

export default function ContourConnectorWorkflow({
    sessionData,
    environmentObjectIds,
    isLoadingSession,
    error,
}: ContourConnectorWorkflowProps) {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const { start: startProcessing, isSubmitting } = useAutoSegmentationTask({
        studyId:
            environmentObjectIds?.primaryImage?.studyInstanceUID ??
            (sessionData?.payload?.context_image.study_instance_uid || ''),
        seriesId:
            environmentObjectIds?.primaryImage?.seriesInstanceUID ??
            (sessionData?.payload?.context_image.series_instance_uid || ''),
        patientId: sessionData?.payload?.patient.id || selectedPatientId || '',
        structureSetSopInstanceUid:
            environmentObjectIds?.structureSet?.sopInstanceUID || '',
    });
    const { data: availableModels = [], isLoading: isLoadingModels } =
        useBackendModels(APIType.CONTOURING, { refetchInterval: 10000 });

    const {
        data: modelCustomizations = [],
        isLoading: isLoadingCustomizations,
    } = useModelCustomizations(APIType.CONTOURING, { refetchInterval: 10000 });

    return (
        <AutoSegmentation
            key={'auto-segmentation-modal'}
            availableModels={availableModels}
            modelCustomizations={
                modelCustomizations as UserCustomization<ContouringCustomizationFile>[]
            }
            onSubmit={startProcessing}
            isLoadingModels={isLoadingModels || isLoadingCustomizations}
            isLoadingSession={isLoadingSession}
            isSubmitting={isSubmitting}
            error={error}
            showPseudonymization={false}
        />
    );
}
